import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Collapse, Empty, List, Space, Spin, Table, Tag, Typography, message
} from 'antd';
import {
  CarOutlined, PlusOutlined, ReloadOutlined, RightOutlined, WarningOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  supabase,
  getCurrentOrgId,
  formatTime,
  getClientTripDisplayColor,
  hasFullCrew,
  mapJobToClientRequestStatus,
  getClientRequestStatusColor,
  isClientRequestOverdueUnscheduled,
  type Trip,
  type Job,
  type JobLeg,
  type ClientRequestStatus,
} from '@ubus/shared';
import {
  translateClientRequestStatus,
  translateClientTripStatus,
} from '../utils/clientStatusLabels';

const { Title, Text } = Typography;

type JobRow = Job & { job_legs: JobLeg[] };

function shortPlace(address?: string | null): string {
  if (!address?.trim()) return '—';
  return address.split(',')[0]?.trim() || address.trim();
}

function tripRoute(trip: Trip): string {
  return `${shortPlace(trip.pickup_address)} → ${shortPlace(trip.dropoff_address)}`;
}

function jobRoute(job: JobRow, t: TFunction): string {
  const leg = job.job_legs?.[0];
  if (leg?.pickup_address && leg?.dropoff_address) {
    return `${shortPlace(leg.pickup_address)} → ${shortPlace(leg.dropoff_address)}`;
  }
  const title = job.title ?? '';
  if (/smoke[-_]?clt|smoke[-_]?test|accept-path|reject-path/i.test(title)) return t('common.tripRequest');
  const after = title.split('·')[1]?.trim();
  return after || title || t('common.tripRequest');
}

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [upcoming, setUpcoming] = useState<Trip[]>([]);
  const [recentPast, setRecentPast] = useState<Trip[]>([]);
  const [requests, setRequests] = useState<JobRow[]>([]);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const today = dayjs().format('YYYY-MM-DD');
  const endUpcoming = dayjs().add(7, 'day').format('YYYY-MM-DD');
  const startPast = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
  const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const orgId = await getCurrentOrgId();
      if (orgId) {
        const { data: org } = await supabase
          .from('organizations')
          .select('name')
          .eq('id', orgId)
          .maybeSingle();
        setOrgName(org?.name ?? null);
      }

      const [upcomingRes, pastRes, reqRes] = await Promise.all([
        supabase
          .from('trips')
          .select('*, vehicles(*), drivers(*)')
          .gte('date', today)
          .lte('date', endUpcoming)
          .neq('status', 'cancelled')
          .order('date', { ascending: true })
          .order('pickup_time', { ascending: true })
          .limit(40),
        supabase
          .from('trips')
          .select('*, vehicles(*), drivers(*)')
          .gte('date', startPast)
          .lte('date', yesterday)
          .in('status', ['completed', 'cancelled'])
          .order('date', { ascending: false })
          .order('pickup_time', { ascending: false })
          .limit(20),
        orgId
          ? supabase
              .from('jobs')
              .select('*, job_legs(*)')
              .eq('org_id', orgId)
              .eq('source', 'client_request')
              .order('schedule_date', { ascending: false })
              .order('created_at', { ascending: false })
              .limit(8)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (upcomingRes.error) throw upcomingRes.error;
      if (pastRes.error) throw pastRes.error;
      if (reqRes.error) throw reqRes.error;

      const jobs = (reqRes.data ?? []) as JobRow[];
      for (const j of jobs) {
        j.job_legs = (j.job_legs ?? []).sort((a, b) => a.sequence - b.sequence);
      }

      setUpcoming((upcomingRes.data ?? []) as Trip[]);
      setRecentPast((pastRes.data ?? []) as Trip[]);
      setRequests(jobs);
    } catch (err) {
      console.error(err);
      message.error(t('dashboard.loadFailed'));
      setUpcoming([]);
      setRecentPast([]);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [today, endUpcoming, startPast, yesterday, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (channelRef.current) supabase.removeChannel(channelRef.current);
    const channel = supabase
      .channel('home-trips-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'trips' },
        () => {
          void load();
        }
      )
      .subscribe();
    channelRef.current = channel;
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [load]);

  const needsAttention = useMemo(() => {
    const items: Array<{
      key: string;
      kind: 'trip' | 'request';
      title: string;
      detail: string;
      actionLabel: string;
      onAction: () => void;
      tone: 'error' | 'warning' | 'info' | 'success';
    }> = [];

    for (const trip of upcoming) {
      if (trip.status === 'in_progress') {
        items.push({
          key: `trip-${trip.id}`,
          kind: 'trip',
          title: t('dashboard.tripInProgress'),
          detail: `${dayjs(trip.date).format('DD MMM')} · ${formatTime(trip.pickup_time)} · ${tripRoute(trip)}`,
          actionLabel: t('common.track'),
          onAction: () => navigate(`/map/${trip.id}`),
          tone: 'success',
        });
      } else if (trip.date === today && (trip.status === 'dispatched' || trip.status === 'scheduled')) {
        const crewReady = trip.status === 'dispatched' && hasFullCrew(trip);
        const vehicleOnly = Boolean(trip.vehicle_id && !trip.driver_id);
        items.push({
          key: `trip-${trip.id}`,
          kind: 'trip',
          title: crewReady
            ? t('dashboard.departingTodayCrewAssigned')
            : vehicleOnly
              ? trip.status === 'dispatched'
                ? t('dashboard.departingTodayDriverPending')
                : t('dashboard.scheduledTodayDriverPending')
              : t('dashboard.scheduledForToday'),
          detail: `${formatTime(trip.pickup_time)} · ${tripRoute(trip)}`,
          actionLabel: crewReady ? t('common.track') : t('common.details'),
          onAction: () =>
            navigate(crewReady ? `/map/${trip.id}` : `/trip/${trip.id}`),
          tone: 'info',
        });
      }
    }

    for (const job of requests) {
      const st = mapJobToClientRequestStatus(job);
      if (st === 'rejected') {
        items.push({
          key: `req-${job.id}`,
          kind: 'request',
          title: t('dashboard.requestRejected'),
          detail: `${dayjs(job.schedule_date).format('DD MMM')} · ${jobRoute(job, t)}${
            job.rejection_reason ? ` — ${job.rejection_reason}` : ''
          }`,
          actionLabel: t('common.details'),
          onAction: () => navigate(`/requests/${job.id}`),
          tone: 'error',
        });
      } else if (isClientRequestOverdueUnscheduled(job)) {
        items.push({
          key: `req-overdue-${job.id}`,
          kind: 'request',
          title: t('dashboard.requestOverdueUnscheduled'),
          detail: `${dayjs(job.schedule_date).format('DD MMM')} · ${jobRoute(job, t)}`,
          actionLabel: t('common.details'),
          onAction: () => navigate(`/requests/${job.id}`),
          tone: 'warning',
        });
      }
    }

    return items;
  }, [upcoming, requests, today, navigate, t]);

  const recentRequests = useMemo(() => requests.slice(0, 5), [requests]);

  const tripColumns: ColumnsType<Trip> = [
    {
      title: t('common.date'),
      dataIndex: 'date',
      width: 110,
      render: (d: string) =>
        d === today ? (
          <Text strong>{t('common.today')}</Text>
        ) : (
          dayjs(d).format('DD MMM')
        ),
    },
    {
      title: t('dashboard.pickupDropoff'),
      key: 'times',
      width: 140,
      render: (_, trip) => {
        const pickup = formatTime(trip.pickup_time);
        const dropoff = trip.estimated_dropoff_time
          ? formatTime(trip.estimated_dropoff_time)
          : null;
        return dropoff ? `${pickup} → ${dropoff}` : pickup;
      },
    },
    {
      title: t('common.route'),
      key: 'route',
      ellipsis: true,
      render: (_, trip) => tripRoute(trip),
    },
    {
      title: t('common.statusLabel'),
      key: 'status',
      width: 120,
      render: (_, trip) => (
        <Tag color={getClientTripDisplayColor(trip)}>
          {translateClientTripStatus(t, trip)}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_, trip) => (
        <Space size={4}>
          <Button type="link" size="small" onClick={() => navigate(`/trip/${trip.id}`)}>
            {t('common.details')}
          </Button>
          {(trip.status === 'dispatched' || trip.status === 'in_progress') && (
            <Button
              type="link"
              size="small"
              icon={<CarOutlined />}
              onClick={() => navigate(`/map/${trip.id}`)}
            >
              {t('common.track')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const requestColumns: ColumnsType<JobRow> = [
    {
      title: t('common.statusLabel'),
      width: 110,
      render: (_, job) => {
        const st = mapJobToClientRequestStatus(job) as ClientRequestStatus;
        const overdue = isClientRequestOverdueUnscheduled(job);
        return (
          <Space size={4} wrap>
            <Tag color={getClientRequestStatusColor(st)}>
              {translateClientRequestStatus(t, st)}
            </Tag>
            {overdue ? <Tag color="error">{t('requests.overdueBadge')}</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: t('common.date'),
      width: 100,
      render: (_, job) => dayjs(job.schedule_date).format('DD MMM'),
    },
    {
      title: t('common.route'),
      ellipsis: true,
      render: (_, job) => jobRoute(job, t),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="client-page-header">
        <div>
          <Title level={3} className="client-page-title">{t('dashboard.title')}</Title>
          <Text className="client-page-subtitle">
            {orgName ? t('dashboard.subtitleWithOrg', { orgName }) : t('dashboard.subtitle')}
          </Text>
        </div>
        <div className="client-actions">
          <Button icon={<ReloadOutlined />} onClick={() => void load()}>
            {t('common.refresh')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/requests/new')}
          >
            {t('nav.newRequest')}
          </Button>
        </div>
      </div>

      {needsAttention.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <Text
            strong
            style={{
              display: 'block',
              marginBottom: 10,
              fontSize: 12,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--client-ink-secondary)',
            }}
          >
            {t('dashboard.needsAttention')}
          </Text>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {needsAttention.map((item) => (
              <Alert
                key={item.key}
                type={item.tone}
                showIcon
                icon={item.tone === 'error' ? <WarningOutlined /> : undefined}
                message={item.title}
                description={item.detail}
                action={
                  <Button size="small" type="primary" ghost={item.tone !== 'success'} onClick={item.onAction}>
                    {item.actionLabel}
                  </Button>
                }
                style={{ borderRadius: 10 }}
              />
            ))}
          </Space>
        </section>
      )}

      <section style={{ marginBottom: 28 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 10,
            gap: 12,
          }}
        >
          <div>
            <Text
              strong
              style={{
                display: 'block',
                fontSize: 12,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--client-ink-secondary)',
              }}
            >
              {t('dashboard.upcoming')}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('dashboard.upcomingHint')}
            </Text>
          </div>
          <Button type="link" onClick={() => navigate('/trips')} style={{ paddingInline: 0 }}>
            {t('dashboard.allTrips')} <RightOutlined />
          </Button>
        </div>

        {upcoming.length === 0 ? (
          <Empty description={t('dashboard.noUpcomingTrips')}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/requests/new')}>
              {t('nav.newRequest')}
            </Button>
          </Empty>
        ) : (
          <Table
            columns={tripColumns}
            dataSource={upcoming}
            rowKey="id"
            pagination={false}
            size="middle"
            scroll={{ x: 640 }}
          />
        )}
      </section>

      <section style={{ marginBottom: 28 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 10,
            gap: 12,
          }}
        >
          <div>
            <Text
              strong
              style={{
                display: 'block',
                fontSize: 12,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--client-ink-secondary)',
              }}
            >
              {t('dashboard.myRequests')}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('dashboard.myRequestsHint')}
            </Text>
          </div>
          <Button type="link" onClick={() => navigate('/requests')} style={{ paddingInline: 0 }}>
            {t('dashboard.allRequests')} <RightOutlined />
          </Button>
        </div>

        {recentRequests.length === 0 ? (
          <Empty description={t('dashboard.noRequestsYet')}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/requests/new')}>
              {t('nav.newRequest')}
            </Button>
          </Empty>
        ) : (
          <Table
            columns={requestColumns}
            dataSource={recentRequests}
            rowKey="id"
            pagination={false}
            size="middle"
            onRow={(job) => ({
              style: { cursor: 'pointer' },
              onClick: () => navigate(`/requests/${job.id}`),
            })}
          />
        )}
      </section>

      <section>
        <Collapse
          ghost
          items={[
            {
              key: 'past',
              label: (
                <span>
                  <Text strong style={{ fontSize: 13 }}>{t('dashboard.recentPast')}</Text>
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 13 }}>
                    {t('dashboard.recentPastHint', { count: recentPast.length })}
                  </Text>
                </span>
              ),
              children:
                recentPast.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('dashboard.noPastTrips')} />
                ) : (
                  <List
                    size="small"
                    dataSource={recentPast}
                    renderItem={(trip) => (
                      <List.Item
                        actions={[
                          <Button
                            key="open"
                            type="link"
                            size="small"
                            onClick={() => navigate(`/trip/${trip.id}`)}
                          >
                            {t('common.open')}
                          </Button>,
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            <Space size={8} wrap>
                              <Text>{dayjs(trip.date).format('DD MMM')}</Text>
                              <Tag color={getClientTripDisplayColor(trip)}>
                                {translateClientTripStatus(t, trip)}
                              </Tag>
                            </Space>
                          }
                          description={tripRoute(trip)}
                        />
                      </List.Item>
                    )}
                  />
                ),
            },
          ]}
        />
      </section>
    </div>
  );
}

export default Home;
